import React from 'react';
import PropTypes from 'prop-types';
import { Document, EyeOpen, Trash } from '../../assets/icons';
import { ENTITY_DOC_UPLOAD_STATUS } from '../../utils/entityDocumentUpload';
import './EntityDocumentItem.scss';

const PROGRESS_RING_RADIUS = 9;
const PROGRESS_RING_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RING_RADIUS;

const ImageFileIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='3' y='3' width='18' height='18' rx='3' stroke='currentColor' strokeWidth='1.5' />
    <circle cx='9' cy='9' r='2' stroke='currentColor' strokeWidth='1.5' />
    <path d='M21 15L16 10L5 21' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const UploadedCheck = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-label='Uploaded'>
    <circle cx='12' cy='12' r='11' fill='currentColor' />
    <path d='M7.5 12L10.5 15L16.5 9' stroke='#ffffff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const ProgressRing = ({ percent }) => (
  <svg width='24' height='24' viewBox='0 0 24 24' className='entity-document-item__ring' aria-hidden='true'>
    <circle cx='12' cy='12' r={PROGRESS_RING_RADIUS} className='entity-document-item__ring__track' />
    <circle
      cx='12'
      cy='12'
      r={PROGRESS_RING_RADIUS}
      className='entity-document-item__ring__bar'
      strokeDasharray={PROGRESS_RING_CIRCUMFERENCE}
      strokeDashoffset={PROGRESS_RING_CIRCUMFERENCE * (1 - percent / 100)}
    />
  </svg>
);

ProgressRing.propTypes = {
  percent: PropTypes.number.isRequired,
};

// One added entity document. While the batch upload runs it shows that file's status
// (Waiting → % with progress ring → green check, or Failed); otherwise preview/remove actions.
const EntityDocumentItem = ({ documentItem, uploadStatus, isUploading, onPreview, onRemove }) => {
  const { state, percent = 0 } = uploadStatus || {};
  const isImage = documentItem.originFileObj?.type?.startsWith('image/');

  const renderStatus = () => {
    switch (state) {
      case ENTITY_DOC_UPLOAD_STATUS.UPLOADING:
        return (
          <span className='entity-document-item__status entity-document-item__status--uploading'>
            {percent}%
            <ProgressRing percent={percent} />
          </span>
        );
      case ENTITY_DOC_UPLOAD_STATUS.DONE:
        return (
          <span className='entity-document-item__status entity-document-item__status--done'>
            <UploadedCheck />
          </span>
        );
      case ENTITY_DOC_UPLOAD_STATUS.ERROR:
        return <span className='entity-document-item__status entity-document-item__status--error'>Failed</span>;
      default:
        return <span className='entity-document-item__status'>Waiting</span>;
    }
  };

  return (
    <div className={`entity-document-item ${state ? `entity-document-item--${state}` : ''}`}>
      <span className='entity-document-item__file-icon'>{isImage ? <ImageFileIcon /> : <Document />}</span>
      <div className='entity-document-item__details'>
        <p className='entity-document-item__name'>{documentItem.fileName}</p>
        <p className='entity-document-item__type'>{documentItem.docType}</p>
      </div>
      {isUploading ? (
        renderStatus()
      ) : (
        <div className='entity-document-item__actions'>
          {state === ENTITY_DOC_UPLOAD_STATUS.ERROR && renderStatus()}
          <EyeOpen className='entity-document-item__action-icon' onClick={onPreview} />
          <Trash className='entity-document-item__action-icon' onClick={onRemove} />
        </div>
      )}
    </div>
  );
};

EntityDocumentItem.propTypes = {
  documentItem: PropTypes.shape({
    fileName: PropTypes.string,
    docType: PropTypes.string,
    originFileObj: PropTypes.object,
  }).isRequired,
  uploadStatus: PropTypes.shape({
    state: PropTypes.oneOf(Object.values(ENTITY_DOC_UPLOAD_STATUS)),
    percent: PropTypes.number,
  }),
  isUploading: PropTypes.bool,
  onPreview: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default EntityDocumentItem;
